import { CommonModule, DatePipe } from '@angular/common';
import { AfterViewInit, Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule, MatIconButton } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatTableDataSource } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { NavigationEnd, Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { firstValueFrom, Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { AppService } from 'src/app/app.service';
import { CourseSelectionDialogComponent } from 'src/app/shared/components/course-selection-dialog/course-selection-dialog.component';
import { LanguageSelectorComponent } from 'src/app/shared/components/language-selector/language-selector.component';
import { CustomContextMenuItem } from 'src/app/shared/components/ui/custom-context-menu/custom-context-menu.component';
import { CustomContextMenuService } from 'src/app/shared/components/ui/custom-context-menu/custom-context-menu.service';
import { InputComponent } from 'src/app/shared/components/ui/input/input.component';
import { AddSectionComponent } from 'src/app/shared/services/add-section/add-section.component';
import { AppConfirmService } from 'src/app/shared/services/app-confirm/app-confirm.service';
import { ToastService } from 'src/app/shared/services/toast/toast.service';
import { ChunkService } from 'src/app/views/content/chunk/chunk.service';
import { NotesService } from '../notes.service';
import { SkeletonComponent } from 'src/app/shared/components/ui/skeleton/skeleton.component';

@Component({
  selector: 'app-notes-list',
  templateUrl: './notes-list.component.html',
  styleUrls: ['./notes-list.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TranslateModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatTooltipModule,
    MatButtonModule,
    InputComponent,
    DatePipe,
    LanguageSelectorComponent,
    SkeletonComponent,
  ],
})
export class NotesListComponent implements OnInit, OnDestroy, AfterViewInit {
  dataSource = new MatTableDataSource();
  displayedColumns = ['id', 'name', 'description', 'actions'];
  notes: any[] = [];
  noteSubscription;
  noteListSubscription;
  routerSubscription: Subscription;
  currentNoteId: string;
  pageIndex = 0;
  totalLength: any;
  searchInput = '';
  pageSizeOptions = [15];
  pageSize = this.pageSizeOptions[0];
  contentEncryptionStatus;
  loading = false;

  @ViewChild('scrollObserver') scrollObserver!: ElementRef;
  private observer: IntersectionObserver;

  constructor(
    private noteService: NotesService,
    private toast: ToastService,
    private translate: TranslateService,
    private dialog: MatDialog,
    private confirm: AppConfirmService,
    private appService: AppService,
    private chunkService: ChunkService,
    private contextMenuService: CustomContextMenuService,
    public router: Router
  ) {
    this.noteSubscription = this.noteService.note$.subscribe((res) => {
      this.getNotes();
    });
    this.noteListSubscription = this.appService.notesList$.subscribe((res) => {
      this.getNotes(false);
    });
    this.routerSubscription = this.router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe((event: NavigationEnd) => {
      this.updateCurrentNoteId(event.urlAfterRedirects);
    });
  }

  ngOnDestroy(): void {
    if (this.observer) {
      this.observer.disconnect();
    }
    this.noteSubscription.unsubscribe();
    this.noteListSubscription.unsubscribe();
    this.routerSubscription.unsubscribe();
  }

  ngOnInit(): void {
    this.getNotes();
    this.updateCurrentNoteId(this.router.url);
  }

  ngAfterViewInit() {
    // this.setupIntersectionObserver();
  }

  setupIntersectionObserver() {
    const options = {
      root: null,
      rootMargin: '100px',
      threshold: 0.1,
    };

    this.observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting && !this.loading && this.notes?.length < this.totalLength) {
          this.loadMore();
        }
      });
    }, options);

    if (this.scrollObserver) {
      this.observer.observe(this.scrollObserver.nativeElement);
    }
  }

  onScroll(event: Event) {
    const el = event.target as HTMLElement;
    if (el.scrollHeight - el.scrollTop - el.clientHeight < 120 && this.totalLength > this.notes.length && !this.loading) {
      this.loadMore();
    }
  }

  loadMore() {
    this.pageIndex++;
    if (this.searchInput) {
      this.search(true);
    } else {
      this.getNotes(true, true);
    }
  }

  async createNote(): Promise<void> {
    const newNote = await this.noteService.createNewNote();
    await this.router.navigate(['/notes', newNote.id]);
  }

  async backToMainMenu() {
    await this.router.navigate(['/knowledge']);
  }

  private updateCurrentNoteId(url: string): void {
    const urlSegments = url.split('/');
    if (urlSegments[1] === 'notes' && urlSegments.length > 2) {
      this.currentNoteId = urlSegments[2];
    } else {
      this.currentNoteId = null;
    }
  }

  getSavedNotes() {
    this.noteService.getSavedNotes({
      pageIndex: this.pageIndex,
      searchString: this.searchInput,
    });
  }

  search(isLoadMore = false) {
    if (!isLoadMore) {
      this.pageIndex = 0;
    }
    this.loading = true;
    this.noteService
      .search({
        pageSize: this.pageSize,
        pageIndex: this.pageIndex,
        searchString: this.searchInput,
      })
      .subscribe({
        next: (data: any) => {
          this.totalLength = data.count;
          const newNotes = data.notes.map((note) => {
            note.buttons = this.getNoteButtons(note);
            return note;
          });

          if (this.pageIndex === 0) {
            this.notes = newNotes;
          } else {
            this.notes = [...this.notes, ...newNotes];
          }
          this.dataSource.data = this.notes || [];
          this.loading = false;
        },
        error: (err) => {
          this.loading = false;
          console.error(err);
          this.toast.snackbar.open(this.translate.instant('Snackbar.Error'), 'Ok', {
            duration: 2000,
          });
        },
      });
  }

  async getNotes(reload = true, isLoadMore = false) {
    if (!isLoadMore) {
      this.pageIndex = 0;
    }
    this.loading = true;
    (
      await this.noteService.getAllNotes(
        {
          pageSize: this.pageSize,
          pageIndex: this.pageIndex,
        },
        reload
      )
    ).subscribe({
      next: (data: any) => {
        this.totalLength = data.count;
        const newNotes = data.notes.map((note) => {
          note.buttons = this.getNoteButtons(note);
          return note;
        });

        if (this.pageIndex === 0) {
          this.notes = newNotes;
        } else {
          this.notes = [...this.notes, ...newNotes];
        }

        if (data.notes.length == 0 && this.pageIndex > 0) {
          this.pageIndex--;
        }

        this.loading = false;
        this.dataSource.data = this.notes || [];
      },
      error: (err) => {
        console.error(err);
        this.toast.snackbar.open(this.translate.instant('Snackbar.Error'), 'Ok', {
          duration: 2000,
        });
        this.loading = false;
      },
    });
  }

  editNote(note) {
    this.loading = true;

    this.dialog
      .open(AddSectionComponent, {
        data: {
          noteID: note._id,
          type: 'edit-note',
        },
        width: '500px',
        autoFocus: false,
      })
      .afterClosed()
      .subscribe(
        (res) => {
          if (res) {
            this.getNotes();
            this.loading = false;
          }
        },
        (err) => {
          this.loading = false;
        }
      );
  }

  deleteNote(data) {
    this.confirm.confirm().subscribe((res) => {
      if (!res) {
        return;
      }
      this.loading = true;
      this.noteService.deleteNote(data).subscribe({
        next: (data: any) => {
          this.getNotes();
          this.loading = false;
        },
        error: (err) => {
          console.error(err);
          this.toast.snackbar.open(this.translate.instant('Snackbar.Error'), 'Ok', {
            duration: 2000,
          });
          this.loading = false;
        },
      });
    });
  }

  resolveNoteButtons(note): CustomContextMenuItem[] {
    const editButton = {
      id: 'edit-note',
      text: 'Global.Edit',
      func: () => this.editNote(note),
    };
    const courseButton = {
      id: 'course-selection',
      text: 'Notes.CopyToCourse',
      func: () => this.openCourseSelectionDialog(note),
    };

    const deleteButton = {
      id: 'delete-note',
      text: 'Global.Delete',
      func: () => this.deleteNote(note),
    };

    return [editButton, courseButton, deleteButton];
  }
  openCourseSelectionDialog(note: any) {
    const dialogRef = this.dialog.open(CourseSelectionDialogComponent, {
      width: '500px',
      autoFocus: false,
    });

    dialogRef.afterClosed().subscribe((selectedCourse) => {
      if (!selectedCourse) return;

      this.confirm
        .confirm({
          message: this.translate.instant('Notes.CopyToCourseConfirm', {
            courseName: selectedCourse.actualName || selectedCourse.name,
          }),
        })
        .subscribe((confirmed) => {
          if (!confirmed) return;

          this.noteService
            .copyToCourse({
              noteId: note._id,
              courseId: selectedCourse._id,
              lectureName: note.name,
            })
            .subscribe({
              next: () => {
                this.toast.snackbar.open(this.translate.instant('Snackbar.Done'), 'Ok', { duration: 2000 });
              },
              error: (err) => {
                console.error(err);
              },
            });
        });
    });
  }
  openNoteMenu(event: MouseEvent, triggerBtn: MatIconButton, note: any): void {
    event.stopPropagation();
    const buttons = this.resolveNoteButtons(note);
    this.contextMenuService.open(triggerBtn._elementRef, buttons, note);
  }

  getNoteButtons(note) {
    return [
      {
        name: this.translate.instant('Global.Edit'),
        func: (data) => {
          this.editNote(note);
        },
        data: {},
      },
      {
        name: this.translate.instant('Global.EncryptContent'),
        func: (data) => {
          this.encryptContent(note.content_id);
        },
        data: {},
        isHidden: () => {
          return !!note.isEncrypted;
        },
      },
      {
        name: this.translate.instant('Global.DecryptContent'),
        func: (data) => {
          this.decryptContent(note.content_id);
        },
        data: {},
        isHidden: () => {
          return !note.isEncrypted;
        },
      },
      {
        name: this.translate.instant('Global.Delete'),
        func: (data) => {
          this.deleteNote(note);
        },
        data: {},
      },
    ];
  }
  checkRow(row) {}
  applyFilter(searchTerm) {}
  async encryptContent(contentID) {
    const response = await firstValueFrom(this.confirm.confirm());
    if (!response) {
      return;
    }
    this.loading = true;
    this.chunkService.encryptContent({ contentID }).subscribe({
      next: (res) => {
        this.getNotes();
        this.loading = false;
        this.toast.snackbar.open(this.translate.instant('Snackbar.Done'), 'Ok', {
          duration: 2000,
        });
      },
    });
  }
  async decryptContent(contentID) {
    const response = await firstValueFrom(this.confirm.confirm());
    if (!response) {
      return;
    }
    this.loading = true;

    this.chunkService.decryptContent({ contentID }).subscribe({
      next: (res) => {
        this.getNotes();
        this.loading = false;

        this.toast.snackbar.open(this.translate.instant('Snackbar.Done'), 'Ok', {
          duration: 2000,
        });
      },
    });
  }
}
